export interface Schedule2Row {
  numChildren: string;
  estimatedDeduction: string;
}

export interface NC4Draft {
  totalAllowances: string;
  additionalAmount: string;
  socialSecurityNumber: string;
  filingStatus: 'single_married_separately' | 'head_of_household' | 'married_jointly_surviving_spouse' | '';
  firstName: string;
  middleInitial: string;
  lastName: string;
  address: string;
  city: string;
  state: string;
  zipCode: string;
  country: string;
  signature: string;

  single_itemizedDeductionsExceed: 'yes' | 'no' | '';
  single_childDeductionExceed: 'yes' | 'no' | '';
  single_federalAdjustments: 'yes' | 'no' | '';
  single_claimTaxCredits: 'yes' | 'no' | '';
  joint_itemizedDeductionsExceed: 'yes' | 'no' | '';
  joint_childDeductionExceed: 'yes' | 'no' | '';
  joint_federalAdjustments: 'yes' | 'no' | '';
  joint_claimTaxCredits: 'yes' | 'no' | '';
  joint_spouseBenefits: 'yes' | 'no' | '';
  separate_itemizedDeductionsExceed: 'yes' | 'no' | '';
  separate_childDeductionExceed: 'yes' | 'no' | '';
  separate_federalAdjustments: 'yes' | 'no' | '';
  separate_claimTaxCredits: 'yes' | 'no' | '';
  hoh_itemizedDeductionsExceed: 'yes' | 'no' | '';
  hoh_childDeductionExceed: 'yes' | 'no' | '';
  hoh_federalAdjustments: 'yes' | 'no' | '';
  hoh_claimTaxCredits: 'yes' | 'no' | '';
  surviving_itemizedDeductionsExceed: 'yes' | 'no' | '';
  surviving_childDeductionExceed: 'yes' | 'no' | '';
  surviving_federalAdjustments: 'yes' | 'no' | '';
  surviving_claimTaxCredits: 'yes' | 'no' | '';

  part2_line1_itemizedDeductions: string;
  part2_line2_standardDeduction: string;
  part2_line3_subtractLine2FromLine1: string;
  part2_line4_childDeductionAmount: string;
  part2_line5_federalAdjustmentsToIncome: string;
  part2_line6_addLines345: string;
  part2_line7_nonwageIncome: string;
  part2_line8_ncAdditionsToAgi: string;
  part2_line9_addLines78: string;
  part2_line10_subtractLine9FromLine6: string;
  part2_line11_divideLine10By2500: string;
  part2_line12_estimatedNcTaxCredits: string;
  part2_line13_divideLine12By109: string;
  part2_line14_additionalAllowances: string;
  part2_line15_totalAllowancesPartII: string;
  part2_line16_spouseAllowancesToClaim: string;
  part2_line17_finalTotalAllowances: string;

  sch1_homeMortgageInterest: string;
  sch1_realEstatePropertyTaxes: string;
  sch1_charitableContributions: string;
  sch1_medicalAndDentalExpenses: string;
  sch1_repaymentOfClaimOfRightIncome: string;
  sch1_totalEstimatedItemizedDeductions: string;

  sch2_single_income_0_20000: Schedule2Row;
  sch2_single_income_20001_30000: Schedule2Row;
  sch2_single_income_30001_40000: Schedule2Row;
  sch2_single_income_40001_50000: Schedule2Row;
  sch2_single_income_50001_60000: Schedule2Row;
  sch2_single_income_60001_70000: Schedule2Row;
  sch2_single_income_over_70000: Schedule2Row;
  sch2_mfj_ss_income_0_40000: Schedule2Row;
  sch2_mfj_ss_income_40001_60000: Schedule2Row;
  sch2_mfj_ss_income_60001_80000: Schedule2Row;
  sch2_mfj_ss_income_80001_100000: Schedule2Row;
  sch2_mfj_ss_income_100001_120000: Schedule2Row;
  sch2_mfj_ss_income_120001_140000: Schedule2Row;
  sch2_mfj_ss_income_over_140000: Schedule2Row;
  sch2_hoh_income_0_30000: Schedule2Row;
  sch2_hoh_income_30001_45000: Schedule2Row;
  sch2_hoh_income_45001_60000: Schedule2Row;
  sch2_hoh_income_60001_75000: Schedule2Row;
  sch2_hoh_income_75001_90000: Schedule2Row;
  sch2_hoh_income_90001_105000: Schedule2Row;
  sch2_hoh_income_over_105000: Schedule2Row;
  sch2_mfs_income_0_20000: Schedule2Row;
  sch2_mfs_income_20001_30000: Schedule2Row;
  sch2_mfs_income_30001_40000: Schedule2Row;
  sch2_mfs_income_40001_50000: Schedule2Row;
  sch2_mfs_income_50001_60000: Schedule2Row;
  sch2_mfs_income_60001_70000: Schedule2Row;
  sch2_mfs_income_over_70000: Schedule2Row;
  sch2_totalEstimatedChildDeduction: string;

  [key: string]: any;
}

export const SCH2_SINGLE_BRACKETS: { key: keyof NC4Draft; label: string }[] = [
  { key: 'sch2_single_income_0_20000', label: '$0 – $20,000' },
  { key: 'sch2_single_income_20001_30000', label: '$20,001 – $30,000' },
  { key: 'sch2_single_income_30001_40000', label: '$30,001 – $40,000' },
  { key: 'sch2_single_income_40001_50000', label: '$40,001 – $50,000' },
  { key: 'sch2_single_income_50001_60000', label: '$50,001 – $60,000' },
  { key: 'sch2_single_income_60001_70000', label: '$60,001 – $70,000' },
  { key: 'sch2_single_income_over_70000', label: 'Over $70,000' },
];

export const SCH2_MFJ_SS_BRACKETS: { key: keyof NC4Draft; label: string }[] = [
  { key: 'sch2_mfj_ss_income_0_40000', label: '$0 – $40,000' },
  { key: 'sch2_mfj_ss_income_40001_60000', label: '$40,001 – $60,000' },
  { key: 'sch2_mfj_ss_income_60001_80000', label: '$60,001 – $80,000' },
  { key: 'sch2_mfj_ss_income_80001_100000', label: '$80,001 – $100,000' },
  { key: 'sch2_mfj_ss_income_100001_120000', label: '$100,001 – $120,000' },
  { key: 'sch2_mfj_ss_income_120001_140000', label: '$120,001 – $140,000' },
  { key: 'sch2_mfj_ss_income_over_140000', label: 'Over $140,000' },
];

export const SCH2_HOH_BRACKETS: { key: keyof NC4Draft; label: string }[] = [
  { key: 'sch2_hoh_income_0_30000', label: '$0 – $30,000' },
  { key: 'sch2_hoh_income_30001_45000', label: '$30,001 – $45,000' },
  { key: 'sch2_hoh_income_45001_60000', label: '$45,001 – $60,000' },
  { key: 'sch2_hoh_income_60001_75000', label: '$60,001 – $75,000' },
  { key: 'sch2_hoh_income_75001_90000', label: '$75,001 – $90,000' },
  { key: 'sch2_hoh_income_90001_105000', label: '$90,001 – $105,000' },
  { key: 'sch2_hoh_income_over_105000', label: 'Over $105,000' },
];

export const SCH2_MFS_BRACKETS: { key: keyof NC4Draft; label: string }[] = [
  { key: 'sch2_mfs_income_0_20000', label: '$0 – $20,000' },
  { key: 'sch2_mfs_income_20001_30000', label: '$20,001 – $30,000' },
  { key: 'sch2_mfs_income_30001_40000', label: '$30,001 – $40,000' },
  { key: 'sch2_mfs_income_40001_50000', label: '$40,001 – $50,000' },
  { key: 'sch2_mfs_income_50001_60000', label: '$50,001 – $60,000' },
  { key: 'sch2_mfs_income_60001_70000', label: '$60,001 – $70,000' },
  { key: 'sch2_mfs_income_over_70000', label: 'Over $70,000' },
];

export const emptySchedule2Row = (): Schedule2Row => ({ numChildren: '', estimatedDeduction: '' });

export const buildEmptyNC4 = (): NC4Draft => ({
  totalAllowances: '0',
  additionalAmount: '',
  socialSecurityNumber: '',
  filingStatus: '',
  firstName: '',
  middleInitial: '',
  lastName: '',
  address: '',
  city: '',
  state: '',
  zipCode: '',
  country: 'USA',
  signature: '',
  single_itemizedDeductionsExceed: '',
  single_childDeductionExceed: '',
  single_federalAdjustments: '',
  single_claimTaxCredits: '',
  joint_itemizedDeductionsExceed: '',
  joint_childDeductionExceed: '',
  joint_federalAdjustments: '',
  joint_claimTaxCredits: '',
  joint_spouseBenefits: '',
  separate_itemizedDeductionsExceed: '',
  separate_childDeductionExceed: '',
  separate_federalAdjustments: '',
  separate_claimTaxCredits: '',
  hoh_itemizedDeductionsExceed: '',
  hoh_childDeductionExceed: '',
  hoh_federalAdjustments: '',
  hoh_claimTaxCredits: '',
  surviving_itemizedDeductionsExceed: '',
  surviving_childDeductionExceed: '',
  surviving_federalAdjustments: '',
  surviving_claimTaxCredits: '',
  part2_line1_itemizedDeductions: '',
  part2_line2_standardDeduction: '',
  part2_line3_subtractLine2FromLine1: '',
  part2_line4_childDeductionAmount: '',
  part2_line5_federalAdjustmentsToIncome: '',
  part2_line6_addLines345: '',
  part2_line7_nonwageIncome: '',
  part2_line8_ncAdditionsToAgi: '',
  part2_line9_addLines78: '',
  part2_line10_subtractLine9FromLine6: '',
  part2_line11_divideLine10By2500: '',
  part2_line12_estimatedNcTaxCredits: '',
  part2_line13_divideLine12By109: '',
  part2_line14_additionalAllowances: '',
  part2_line15_totalAllowancesPartII: '',
  part2_line16_spouseAllowancesToClaim: '',
  part2_line17_finalTotalAllowances: '',
  sch1_homeMortgageInterest: '',
  sch1_realEstatePropertyTaxes: '',
  sch1_charitableContributions: '',
  sch1_medicalAndDentalExpenses: '',
  sch1_repaymentOfClaimOfRightIncome: '',
  sch1_totalEstimatedItemizedDeductions: '',
  sch2_single_income_0_20000: emptySchedule2Row(),
  sch2_single_income_20001_30000: emptySchedule2Row(),
  sch2_single_income_30001_40000: emptySchedule2Row(),
  sch2_single_income_40001_50000: emptySchedule2Row(),
  sch2_single_income_50001_60000: emptySchedule2Row(),
  sch2_single_income_60001_70000: emptySchedule2Row(),
  sch2_single_income_over_70000: emptySchedule2Row(),
  sch2_mfj_ss_income_0_40000: emptySchedule2Row(),
  sch2_mfj_ss_income_40001_60000: emptySchedule2Row(),
  sch2_mfj_ss_income_60001_80000: emptySchedule2Row(),
  sch2_mfj_ss_income_80001_100000: emptySchedule2Row(),
  sch2_mfj_ss_income_100001_120000: emptySchedule2Row(),
  sch2_mfj_ss_income_120001_140000: emptySchedule2Row(),
  sch2_mfj_ss_income_over_140000: emptySchedule2Row(),
  sch2_hoh_income_0_30000: emptySchedule2Row(),
  sch2_hoh_income_30001_45000: emptySchedule2Row(),
  sch2_hoh_income_45001_60000: emptySchedule2Row(),
  sch2_hoh_income_60001_75000: emptySchedule2Row(),
  sch2_hoh_income_75001_90000: emptySchedule2Row(),
  sch2_hoh_income_90001_105000: emptySchedule2Row(),
  sch2_hoh_income_over_105000: emptySchedule2Row(),
  sch2_mfs_income_0_20000: emptySchedule2Row(),
  sch2_mfs_income_20001_30000: emptySchedule2Row(),
  sch2_mfs_income_30001_40000: emptySchedule2Row(),
  sch2_mfs_income_40001_50000: emptySchedule2Row(),
  sch2_mfs_income_50001_60000: emptySchedule2Row(),
  sch2_mfs_income_60001_70000: emptySchedule2Row(),
  sch2_mfs_income_over_70000: emptySchedule2Row(),
  sch2_totalEstimatedChildDeduction: '',
});

export const PART2_LINES: { key: keyof NC4Draft; label: string }[] = [
  { key: 'part2_line1_itemizedDeductions', label: 'Line 1 — Total estimated itemized deductions (from Schedule 1)' },
  { key: 'part2_line2_standardDeduction', label: 'Line 2 — Standard deduction' },
  { key: 'part2_line3_subtractLine2FromLine1', label: 'Line 3 — Subtract Line 2 from Line 1' },
  { key: 'part2_line4_childDeductionAmount', label: 'Line 4 — Child deduction amount (from Schedule 2)' },
  { key: 'part2_line5_federalAdjustmentsToIncome', label: 'Line 5 — Federal adjustments to income' },
  { key: 'part2_line6_addLines345', label: 'Line 6 — Add Lines 3, 4, and 5' },
  { key: 'part2_line7_nonwageIncome', label: 'Line 7 — Estimated non-wage income (interest, dividends, etc.)' },
  { key: 'part2_line8_ncAdditionsToAgi', label: 'Line 8 — Estimated NC additions to federal AGI' },
  { key: 'part2_line9_addLines78', label: 'Line 9 — Add Lines 7 and 8' },
  { key: 'part2_line10_subtractLine9FromLine6', label: 'Line 10 — Subtract Line 9 from Line 6' },
  { key: 'part2_line11_divideLine10By2500', label: 'Line 11 — Divide Line 10 by $2,500' },
  { key: 'part2_line12_estimatedNcTaxCredits', label: 'Line 12 — Estimated NC tax credits' },
  { key: 'part2_line13_divideLine12By109', label: 'Line 13 — Divide Line 12 by 1.09' },
  { key: 'part2_line14_additionalAllowances', label: 'Line 14 — Additional allowances' },
  { key: 'part2_line15_totalAllowancesPartII', label: 'Line 15 — Total allowances for Part II' },
  { key: 'part2_line16_spouseAllowancesToClaim', label: 'Line 16 — Allowances spouse will claim' },
  {
    key: 'part2_line17_finalTotalAllowances',
    label: 'Line 17 — Final total allowances (subtract Line 16 from Line 15)',
  },
];
